import { INoteViewMediaSyncManager, INoteViewMediaSyncManagerInjectable, MediaCmd, IMediaSyncInfo, PlayerType } from './note-view-media-sync-manager.interface';
import SyncElementKey from './sync-element-key';
import { isFunction } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { H5_PLAY_EVENT } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import type { SuperPlayer } from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';

/**
 * 备注视图场景下，同步媒体播放状态
 */
export class NoteViewMediaSyncManager implements INoteViewMediaSyncManager {
    private serviceProxy;
    private injectable: INoteViewMediaSyncManagerInjectable;

    public constructor(injectable: INoteViewMediaSyncManagerInjectable) {
        this.injectable = injectable;
    }

    public initServiceProxy = (serviceProxy): void => {
        this.serviceProxy = serviceProxy;
    };

    public attachMediaEvent = (mediaInstance, mediaId: string, playType: PlayerType): void => {
        if (this.injectable.attachMediaEvent(mediaInstance, mediaId, playType)) return;

        if (!this.serviceProxy || !isFunction(mediaInstance?.on)) return;
        const mediaEventMap = new Map([
            [MediaCmd.PLAY, H5_PLAY_EVENT.VIDEO_PLAYING],
            [MediaCmd.PAUSE, H5_PLAY_EVENT.VIDEO_PAUSE],
            [MediaCmd.SPEED, H5_PLAY_EVENT.VIDEO_RATE_CHANGE],
        ]);

        mediaEventMap.forEach((mediaEvent, cmd) => {
            mediaInstance.on(mediaEvent, ({ target }: any) => {
                let syncParams: IMediaSyncInfo = { cmd, mediaId, playtime: target.playtime };
                if (cmd === MediaCmd.SPEED) {
                    syncParams = { ...syncParams, speed: target.playbackRate };
                }
                // 被动同步的行为不用POSTMessage，主动的行为需要postmessage
                if (mediaInstance.isPassiveTrigger) {
                    mediaInstance.isPassiveTrigger = false;
                    return;
                }
                this.serviceProxy.syncVideoState(syncParams);
            });
        });
    };

    public syncVideoState = (mediaInfo: IMediaSyncInfo): void => {
        const { mediaInstance, pictureModelKey } = SyncElementKey.findMediaById(mediaInfo.mediaId) || {};
        if (!pictureModelKey) {
            return;
        }
        SyncElementKey.updateElementKey(pictureModelKey);

        this.handleMediaCmd(mediaInstance as (SuperPlayer | null), mediaInfo, pictureModelKey);
    };

    public handleMediaCmd = (
        mediaIns: SuperPlayer | null,
        mediaInfo: IMediaSyncInfo,
        pictureModelKey: string,
    ): void => {
        if (mediaIns) {
            (mediaIns as any).isPassiveTrigger = true;
        }
        const { cmd, playtime, speed } = mediaInfo;
        const handlerMap: Map<MediaCmd, () => void> = new Map([
            [MediaCmd.PLAY, () => this.playHandler(mediaIns, pictureModelKey, playtime)],
            [MediaCmd.PAUSE, () => this.pauseHandler(mediaIns)],
            [MediaCmd.SPEED, () => this.speedHandler(mediaIns, speed)],
        ]);
        handlerMap.get(cmd)?.();
    };

    public destroy(): void {
        this.serviceProxy?.destory();
        this.serviceProxy = null;
    }

    private playHandler(mediaIns: SuperPlayer | null, pictureModelKey: string, playtime?: number): void {
        if (mediaIns) {
            mediaIns.seek(playtime ?? 0);
            // seek 调用后好像不自动播放，再调下 play
            mediaIns.play();
        } else {
            SyncElementKey.playVideo(pictureModelKey);
        }
    }

    private pauseHandler(mediaIns: SuperPlayer | null): void {
        if (mediaIns) {
            mediaIns.pause();
        }
    }

    private speedHandler(mediaIns: SuperPlayer | null, speed?: number): void {
        if (mediaIns) {
            mediaIns.playbackRate = speed ?? 1;
        }
    }
}
